import { Injectable } from '@angular/core';

export interface MonacoEditorConfig {
  theme: string;
  language: string;
  automaticLayout: boolean;
  minimap: { enabled: boolean };
  wordWrap: string;
  lineNumbers: string;
  readOnly?: boolean;
  fontSize: number;
  fontFamily: string;
  scrollBeyondLastLine: boolean;
  contextmenu?: boolean;
  folding?: boolean;
  selectOnLineNumbers?: boolean;
  quickSuggestions?: boolean;
  suggestOnTriggerCharacters?: boolean;
  acceptSuggestionOnEnter?: string;
  tabCompletion?: string;
  roundedSelection?: boolean;
  scrollbar?: {
    vertical: string;
    horizontal: string;
    verticalScrollbarSize: number;
    horizontalScrollbarSize: number;
  };
  overviewRulerLanes?: number;
  hideCursorInOverviewRuler?: boolean;
  overviewRulerBorder?: boolean;
  lineHeight?: number;
  renderLineHighlight?: string;
  glyphMargin?: boolean;
  lineDecorationsWidth?: number;
  lineNumbersMinChars?: number;
  renderSideBySide?: boolean;
  ignoreTrimWhitespace?: boolean;
  renderIndicators?: boolean;
  originalEditable?: boolean;
  modifiedEditable?: boolean;
}

export type EditorMode = 'editing' | 'display' | 'diff' | 'context';
export type SupportedLanguage =
  | 'sql'
  | 'python'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'yaml'
  | 'xml'
  | 'html'
  | 'css'
  | 'java'
  | 'scala'
  | 'pyspark'
  | 'sparksql'
  | 'text';

@Injectable({
  providedIn: 'root',
})
export class MonacoConfigService {
  private readonly COMMON_FONT_FAMILY = 'JetBrains Mono, Consolas, Monaco, "Fira Code", "Ubuntu Mono", monospace';

  private readonly LANGUAGE_MAP: Record<string, string> = {
    sql: 'sql',
    python: 'python',
    pyspark: 'python',
    sparksql: 'sql',
    javascript: 'javascript',
    typescript: 'typescript',
    json: 'json',
    yaml: 'yaml',
    xml: 'xml',
    html: 'html',
    css: 'css',
    java: 'java',
    scala: 'scala',
  };

  private readonly THEME_MAP: Record<string, string> = {
    sql: 'sql-enhanced',
    python: 'python-dark',
    json: 'vs-dark',
    default: 'vs',
  };

  public getMonacoLanguage(language: string): string {
    return this.LANGUAGE_MAP[language.toLowerCase()] || 'text';
  }

  public getThemeForLanguage(language: string): string {
    const normalizedLang = language.toLowerCase();

    return this.THEME_MAP[normalizedLang] || this.THEME_MAP['default'];
  }

  public getEditorConfig(
    language: string,
    mode: EditorMode = 'display',
    options: Partial<MonacoEditorConfig> = {},
  ): MonacoEditorConfig {
    const monacoLanguage = this.getMonacoLanguage(language);
    const theme = this.getThemeForLanguage(language);

    const baseConfig: MonacoEditorConfig = {
      theme,
      language: monacoLanguage,
      automaticLayout: true,
      minimap: { enabled: false },
      wordWrap: 'on',
      lineNumbers: 'on',
      fontSize: 14,
      fontFamily: this.COMMON_FONT_FAMILY,
      scrollBeyondLastLine: false,
    };

    switch (mode) {
      case 'editing':
        return {
          ...baseConfig,
          readOnly: false,
          folding: true,
          selectOnLineNumbers: true,
          contextmenu: true,
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          acceptSuggestionOnEnter: 'on',
          tabCompletion: 'on',
          ...options,
        };

      case 'display':
        return {
          ...baseConfig,
          readOnly: true,
          contextmenu: false,
          folding: false,
          glyphMargin: false,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 3,
          renderLineHighlight: 'none',
          roundedSelection: false,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
            verticalScrollbarSize: 8,
            horizontalScrollbarSize: 8,
          },
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          overviewRulerBorder: false,
          fontSize: 13,
          lineHeight: 18,
          selectOnLineNumbers: true,
          ...options,
        };

      case 'diff':
        return {
          ...baseConfig,
          readOnly: true,
          renderSideBySide: false,
          ignoreTrimWhitespace: false,
          renderIndicators: true,
          originalEditable: false,
          modifiedEditable: false,
          ...options,
        };

      case 'context':
        return {
          ...baseConfig,
          theme: 'vs-dark',
          language: 'json',
          readOnly: true,
          folding: true,
          contextmenu: true,
          renderLineHighlight: 'none',
          glyphMargin: false,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 3,
          ...options,
        };

      default:
        return {
          ...baseConfig,
          readOnly: true,
          ...options,
        };
    }
  }

  public createDiffModel(
    originalCode: string,
    modifiedCode: string,
    language: string,
  ): { original: unknown; modified: unknown } | null {
    const monaco = (window as any).monaco;
    if (!monaco) return null;

    const monacoLanguage = this.getMonacoLanguage(language);

    const originalModel = monaco.editor.createModel(originalCode, monacoLanguage);
    const modifiedModel = monaco.editor.createModel(modifiedCode, monacoLanguage);

    return {
      original: originalModel,
      modified: modifiedModel,
    };
  }

  public formatSqlCode(code: string): string {
    return code
      .replace(/\s+/g, ' ')
      .replace(/,/g, ',\n  ')
      .replace(/\bFROM\b/gi, '\nFROM')
      .replace(/\bWHERE\b/gi, '\nWHERE')
      .replace(/\bGROUP BY\b/gi, '\nGROUP BY')
      .replace(/\bORDER BY\b/gi, '\nORDER BY')
      .replace(/\bHAVING\b/gi, '\nHAVING')
      .replace(/\bSELECT\b/gi, 'SELECT')
      .trim();
  }

  public getLanguageIcon(language: string): string {
    const iconMap: Record<string, string> = {
      sql: 'pi pi-database',
      python: 'pi pi-code',
      javascript: 'pi pi-code',
      typescript: 'pi pi-code',
      json: 'pi pi-file',
      default: 'pi pi-file',
    };

    return iconMap[language.toLowerCase()] || iconMap['default'];
  }

  public getLanguageLabel(language: string): string {
    const labelMap: Record<string, string> = {
      sql: 'SQL',
      python: 'Python',
      javascript: 'JavaScript',
      typescript: 'TypeScript',
      json: 'JSON',
      yaml: 'YAML',
      xml: 'XML',
      html: 'HTML',
      css: 'CSS',
      java: 'Java',
      scala: 'Scala',
      pyspark: 'PySpark',
      sparksql: 'Spark SQL',
    };

    return labelMap[language.toLowerCase()] || language.toUpperCase();
  }
}
